import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Separator } from "@/components/ui/separator";
import { FaBookOpen, FaExternalLinkAlt, FaPlayCircle, FaGamepad } from "react-icons/fa";
import MisinformationGame from "@/components/MisinformationGame";

// Import resource components
import MediaLiteracy101 from "@/components/resources/MediaLiteracy101";
import SpottingDeepfakesGuide from "@/components/resources/SpottingDeepfakesGuide";
import FactCheckingComparison from "@/components/resources/FactCheckingComparison";
import MisinformationQuiz from "@/components/resources/MisinformationQuiz";

const mediaTypes = [
  { title: "Satire", description: "Content that uses humor, irony, or exaggeration to comment on current events.", style: "border-blue-500/30 bg-blue-500/5" },
  { title: "Misleading Content", description: "Information presented to frame an issue or individual in a certain light by selecting facts selectively.", style: "border-yellow-500/30 bg-yellow-500/5" },
  { title: "False Context", description: "Genuine content shared with false contextual information.", style: "border-orange-500/30 bg-orange-500/5" },
  { title: "Imposter Content", description: "When genuine sources are impersonated with false, made-up sources.", style: "border-purple-500/30 bg-purple-500/5" },
  { title: "Manipulated Content", description: "Genuine information or imagery that is manipulated to deceive.", style: "border-red-500/30 bg-red-500/5" },
  { title: "Fabricated Content", description: "New content that is 100% false, designed to deceive and harm.", style: "border-red-700/30 bg-red-700/5" }
];

const disinformationTactics = [
  "Use of emotional language to provoke strong reactions",
  "Presenting opinions as facts without clear distinction",
  "Cherry picking statistics or facts out of context",
  "Creating false equivalence between two unequal positions",
  "Using misleading headlines that contradict article content",
  "Making unverified claims based on anonymous sources",
  "Falsely attributing quotes or images",
  "Using deceptive editing to alter meaning or context"
];

const resources = [
  {
    title: "Media Literacy 101",
    type: "Course",
    description: "A comprehensive introduction to media literacy skills for the digital age",
    link: "#"
  },
  {
    title: "Spotting Deepfakes Guide",
    type: "Guide",
    description: "Learn how to identify AI-generated or manipulated media",
    link: "#"
  },
  {
    title: "Fact-Checking Websites Comparison",
    type: "Article",
    description: "Overview of major fact-checking organizations and their methodologies",
    link: "#"
  },
  {
    title: "Misinformation Quiz Challenge",
    type: "Interactive",
    description: "Test your ability to identify various types of misleading content",
    link: "#"
  }
];

const Learn = () => {
  const [showGame, setShowGame] = useState(false);
  const [activeResource, setActiveResource] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("types");

  const learnTabs = [
    { value: "types", label: "Types of Misinformation" },
    { value: "tactics", label: "Common Tactics" },
    { value: "resources", label: "Learning Resources" },
  ];

  const renderResourceModal = () => {
    switch (activeResource) {
      case "Media Literacy 101":
        return <MediaLiteracy101 onClose={() => setActiveResource(null)} />;
      case "Spotting Deepfakes Guide":
        return <SpottingDeepfakesGuide onClose={() => setActiveResource(null)} />;
      case "Fact-Checking Websites Comparison":
        return <FactCheckingComparison onClose={() => setActiveResource(null)} />;
      case "Misinformation Quiz Challenge":
        return <MisinformationQuiz onClose={() => setActiveResource(null)} />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderResourceModal()}
      <div className="min-h-screen flex items-center justify-center bg-gray-900 py-4 px-1 sm:px-2 md:px-8 lg:px-16">
        <div className="w-full max-w-5xl p-2 sm:p-4 space-y-4 sm:space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="hidden sm:block">
              <TabsList className="grid w-full grid-cols-3 gap-2 bg-gray-800/60 p-1 rounded-lg backdrop-blur-sm">
                {learnTabs.map((tab) => (
                  <TabsTrigger 
                    key={tab.value} 
                    value={tab.value} 
                    className="data-[state=active]:bg-truth data-[state=active]:text-black hover:bg-truth/20 transition-all duration-150 ease-in-out py-2.5"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="sm:hidden mb-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full flex justify-between items-center text-base py-3 px-4 bg-gray-800/60 border-gray-700 hover:bg-gray-700/80 focus:ring-truth">
                    {learnTabs.find(tab => tab.value === activeTab)?.label || "Select Category"}
                    <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] md:w-full bg-gray-800 border-gray-700 text-gray-200">
                  {learnTabs.map((tab) => (
                    <DropdownMenuItem 
                      key={tab.value} 
                      onSelect={() => setActiveTab(tab.value)}
                      className={`py-2.5 text-base focus:bg-truth/30 focus:text-white ${activeTab === tab.value ? 'bg-truth/20 text-truth' : ''}`}
                    >
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TabsContent value="types" className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Understanding Types of Misinformation</h2>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                Understanding different types of false or misleading information can help you identify them more easily.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {mediaTypes.map((type, index) => (
                  <Card key={index} className={`bg-black/20 backdrop-blur-sm ${type.style} rounded-md sm:rounded-lg p-3 sm:p-4`}>
                    <h3 className="font-medium text-base mb-1">{type.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-300">{type.description}</p>
                  </Card>
                ))}
              </div>
              
              <div className="mt-6 sm:mt-8">
                <h3 className="text-lg font-bold mb-3">The ESCAPE Method</h3>
                <p className="text-gray-400 mb-4 text-sm">
                  Use this mnemonic device to evaluate information you encounter online:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <EscapeMethodCard 
                    letter="E" 
                    title="Evidence" 
                    description="What evidence is provided? Is it verifiable? Is it from primary or secondary sources?"
                  />
                  <EscapeMethodCard 
                    letter="S" 
                    title="Source" 
                    description="Who created this? What's their expertise? Do they have an agenda or bias?"
                  />
                  <EscapeMethodCard 
                    letter="C" 
                    title="Context" 
                    description="When was this created? Has anything changed since then? Is the full context provided?"
                  />
                  <EscapeMethodCard 
                    letter="A" 
                    title="Audience" 
                    description="Who is the intended audience? How might that affect the presentation?"
                  />
                  <EscapeMethodCard 
                    letter="P" 
                    title="Purpose" 
                    description="Why was this created? To inform, persuade, entertain, or sell something?"
                  />
                  <EscapeMethodCard 
                    letter="E" 
                    title="Execution" 
                    description="How is the information presented? What techniques are used to convey the message?"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="tactics" className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Common Disinformation Tactics</h2>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                Recognizing these common tactics can help you identify potentially misleading content.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                {disinformationTactics.map((tactic, index) => (
                  <div key={index} className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-md sm:rounded-lg p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="bg-truth/20 text-truth rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs sm:text-sm">
                        {index + 1}
                      </span>
                      <p className="text-gray-300 text-xs sm:text-sm">{tactic}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 sm:mt-8">
                <Card className="bg-black/20 backdrop-blur-sm border-truth/30 rounded-md sm:rounded-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <FaGamepad className="h-5 w-5 text-truth" />
                      Interactive Learning: Misinformation Game
                    </CardTitle>
                    <CardDescription>
                      Test your ability to spot misinformation with our interactive quiz game
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs sm:text-sm text-gray-300">
                      This interactive game presents real-world examples of misinformation and challenges you to identify the tactics being used.
                      It's a fun way to sharpen your critical evaluation skills.
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="ml-auto bg-truth hover:bg-truth-dark"
                      onClick={() => setShowGame(true)}
                    >
                      Play Now
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="resources" className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Explore Learning Resources</h2>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                Explore these resources to deepen your understanding of misinformation and improve your media literacy skills.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                {resources.map((resource, index) => (
                  <Card key={index} className="bg-black/20 backdrop-blur-sm border-gray-800 rounded-md sm:rounded-lg p-3 sm:p-6">
                    <CardHeader className="p-0 mb-1 sm:mb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base sm:text-lg">{resource.title}</CardTitle>
                        <Badge type={resource.type as any} />
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="text-xs sm:text-sm text-gray-300">{resource.description}</p>
                    </CardContent>
                    <CardFooter className="p-0 pt-2 sm:pt-4">
                      <Button 
                        variant="ghost" 
                        className="ml-auto text-xs sm:text-sm flex items-center gap-2"
                        onClick={() => setActiveResource(resource.title)}
                      >
                        Access Resource <FaExternalLinkAlt className="h-3 w-3" />
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
              
              <Separator className="my-6 sm:my-8" />
              
            </TabsContent>
          </Tabs>
          
          {showGame ? (
            <div>
              <Button 
                variant="outline" 
                onClick={() => setShowGame(false)} 
                className="mb-4"
              >
                ← Back to Resources
              </Button>
              <MisinformationGame />
            </div>
          ) : (
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800 mt-8">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center text-center md:text-left">
                  <div className="rounded-full bg-truth/20 p-4 shrink-0">
                    <FaBookOpen className="h-8 w-8 text-truth" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">Ready to test your knowledge?</h3>
                    <p className="text-gray-400 mb-4">
                      Take our interactive misinformation quiz to test your ability to spot misleading content. 
                      You'll be presented with real-world examples and asked to identify issues.
                    </p>
                    <Button 
                      className="bg-truth hover:bg-truth-dark"
                      onClick={() => setShowGame(true)}
                    >
                      Take the Quiz
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

interface BadgeProps {
  type: "Course" | "Guide" | "Article" | "Interactive";
}

function Badge({ type }: BadgeProps) {
  const getTypeStyles = () => {
    switch (type) {
      case "Course":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "Guide":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "Article":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "Interactive":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  return (
    <span className={`text-xs px-2 py-1 rounded-full border ${getTypeStyles()}`}>
      {type}
    </span>
  );
}

interface EscapeMethodCardProps {
  letter: string;
  title: string;
  description: string;
}

function EscapeMethodCard({ letter, title, description }: EscapeMethodCardProps) {
  return (
    <div className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-5 flex gap-4">
      <div className="bg-truth/20 text-truth rounded-full w-10 h-10 flex items-center justify-center font-bold text-xl shrink-0">
        {letter}
      </div>
      <div>
        <h3 className="font-semibold text-lg mb-1">{title}</h3>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    </div>
  );
}

export default Learn;
